/**
 * UI 그림 해석 — 게시본 → 번들 기본 → 이모지.
 *
 * 경로를 컴포넌트에 박아 두면 운영자가 못 바꾼다. 목록은 shared `UI_ART` 하나이고
 * 여기서는 "지금 무엇을 그릴지"만 정한다.
 */
import { useEffect, useState } from "react";
import { uiArtKey, uiArtSlot, type UiArtGroup } from "@greg-chick/shared";
import { loadAssetManifest, assetManifestNow, type AssetManifest } from "./assetManifest";
import { safeImageUrl } from "./safeUrl";

/** 지금 그릴 그림 URL(없으면 undefined → 호출부가 이모지로). */
export function resolveUiArt(
  manifest: AssetManifest | null,
  group: UiArtGroup,
  name: string,
): string | undefined {
  const published = safeImageUrl(manifest?.assets[uiArtKey(group, name)]?.url);
  return published ?? uiArtSlot(group, name)?.bundled;
}

/**
 * 매니페스트를 한 번 받아 두고 해석기를 준다.
 * `loadAssetManifest` 가 캐시하므로 여러 컴포넌트가 불러도 요청은 한 번이다.
 */
export function useUiArt(): (group: UiArtGroup, name: string) => string | undefined {
  // 기다리지 않는다 — 예전엔 첫 1초 동안 위·아래 아이콘이 통째로 비어 보였다.
  const [manifest, setManifest] = useState<AssetManifest | null>(assetManifestNow);
  useEffect(() => {
    let alive = true;
    void loadAssetManifest().then((m) => alive && setManifest(m));
    return () => {
      alive = false;
    };
  }, []);
  return (group, name) => resolveUiArt(manifest, group, name);
}

/**
 * 🧩 어디서 온 그림인가까지 — 운영자가 올린 것(`published`)이면 화면 묶음보다 이긴다(`UiIcon`).
 * 정적·게시 매니페스트 어디에도 `ui.*` 기본 항목이 없으므로(2026-09-27 확인) 매니페스트에 주소가 있으면 곧 운영자 것이다.
 */
export function useUiArtEx(): (
  group: UiArtGroup,
  name: string,
) => { url: string | undefined; from: "published" | "bundled" | undefined } {
  const [manifest, setManifest] = useState<AssetManifest | null>(assetManifestNow);
  useEffect(() => {
    let alive = true;
    void loadAssetManifest().then((m) => alive && setManifest(m));
    return () => {
      alive = false;
    };
  }, []);
  return (group, name) => {
    const published = safeImageUrl(manifest?.assets[uiArtKey(group, name)]?.url);
    if (published) return { url: published, from: "published" };
    const bundled = uiArtSlot(group, name)?.bundled;
    return { url: bundled, from: bundled ? "bundled" : undefined };
  };
}
